import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import PwaInstallPrompt from "./components/PwaInstallPrompt";

import { AuthProvider } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import SponsorThankYou from "@/pages/SponsorThankYou";
import SponsorCancelled from "@/pages/SponsorCancelled";
import Dashboard from "@/pages/Dashboard";
import Book from "@/pages/Book";
import SessionDetail from "@/pages/SessionDetail";
import Chat from "@/pages/Chat";
import SelfHelp from "@/pages/SelfHelp";
import Availability from "@/pages/Availability";
import AdminPanel from "@/pages/AdminPanel";
import AdminUserDetail from "@/pages/AdminUserDetail";
import Profile from "@/pages/Profile";
import CaseFiles from "@/pages/CaseFiles";
import CaseFile from "@/pages/CaseFile";
import CounsellorApply from "@/pages/CounsellorApply";

// Static role guards — extracted so they're not recreated on every render
const CLIENT_OR_PARENT_ROLES = ["client", "parent"];
const STAFF_ROLES = ["counsellor", "admin"];
const ADMIN_ROLES = ["admin"];
const TOAST_OPTIONS = { style: { borderRadius: "16px", fontFamily: "Manrope, system-ui" } };

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect Emergent OAuth callback synchronously before any other route runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/sponsor/thank-you" element={<SponsorThankYou />} />
      <Route path="/sponsor/cancelled" element={<SponsorCancelled />} />
      <Route path="/counsellor-apply" element={<CounsellorApply />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/book" element={<ProtectedRoute roles={CLIENT_OR_PARENT_ROLES}><Book /></ProtectedRoute>} />
      <Route path="/session/:bookingId" element={<ProtectedRoute><SessionDetail /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/self-help" element={<ProtectedRoute><SelfHelp /></ProtectedRoute>} />
      <Route path="/availability" element={<ProtectedRoute roles={STAFF_ROLES}><Availability /></ProtectedRoute>} />
      <Route path="/case-files" element={<ProtectedRoute roles={STAFF_ROLES}><CaseFiles /></ProtectedRoute>} />
      <Route path="/case-file/:clientId" element={<ProtectedRoute roles={STAFF_ROLES}><CaseFile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminUserDetail /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ChatProvider>
            <AppRouter />
            <PwaInstallPrompt />
            <Toaster position="top-center" toastOptions={TOAST_OPTIONS} />
          </ChatProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
